import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@chakra-ui/react';
import { Upload } from 'lucide-react';
import { importSketchupShortcuts } from '../../utils/sketchupShortcuts';
import { notify } from '../../utils/notify';

/**
 * "Import SketchUp Shortcuts..." button for the Keyboard Shortcuts editor
 * footer. Reads a SketchUp preferences file (Window > Preferences >
 * Shortcuts > Export), resolves each menu path to the equivalent app
 * command — the Sketch workbench tools in particular — and applies the
 * shortcut strings as user shortcuts (persisted by the command store).
 */
export function SketchupShortcutImportButton() {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleImportFile = async (file: File) => {
        try {
            const summary = importSketchupShortcuts(await file.text());
            if (summary.total === 0) {
                notify('No shortcuts found in the SketchUp shortcuts file.', 'warning');
                return;
            }
            const parts = [`${summary.applied.length} shortcut(s) applied`];
            if (summary.skipped.length > 0) parts.push(`${summary.skipped.length} skipped`);
            if (summary.unparsedLines > 0) parts.push(`${summary.unparsedLines} unrecognized line(s)`);
            notify(
                `SketchUp shortcuts imported: ${parts.join(', ')}.`,
                summary.applied.length > 0 ? 'success' : 'warning'
            );
            // Per-entry details live in the console: the toast only carries totals.
            const unmapped = summary.skipped.filter((s) => s.reason === 'unmapped-command');
            if (unmapped.length > 0) {
                console.warn(
                    '[SketchupShortcuts] SketchUp commands without an app equivalent:',
                    unmapped.map((s) => `${s.shortcut} → ${s.commandPath}`)
                );
            }
            for (const collision of summary.collisions) {
                console.warn(
                    `[SketchupShortcuts] Shortcut '${collision.shortcut}' also remains assigned to '${collision.commandLabel}'.`
                );
            }
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unknown error.';
            notify(`SketchUp shortcut import failed: ${message}`, 'error');
        }
    };

    return (
        <>
            <Button
                size="sm"
                variant="outline"
                leftIcon={<Upload size={14} />}
                flexShrink={0}
                whiteSpace="nowrap"
                onClick={() => fileInputRef.current?.click()}
            >
                {t('dialogs.ui.importSketchupShortcuts')}
            </Button>
            <input
                ref={fileInputRef}
                type="file"
                accept=".dat,.txt,text/plain"
                className="hidden-file-input"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleImportFile(file);
                    e.target.value = '';
                }}
            />
        </>
    );
}
